import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { MultilingualLabel, PictureSpot } from '@sparted/shared-library/business/types';
import { DeepPartial } from 'ts-essentials';
import { t } from 'i18next';

import SGameplayHeader from 'Components/structural/SGameplayHeader/SGameplayHeader';
import SPictureSpotImage from 'Components/structural/SPictureSpotImage/SPictureSpotImage';
import STabList from 'Components/structural/STabList/STabList';
import STextArea from 'Components/structural/STextArea/STextArea';
import UButton from 'Components/unit/UButton/UButton';
import withTooltip from 'Components/hoc/withTooltip/withTooltip';
import { UFieldError } from 'Components/unit/UFieldError/UFieldError';
import SwitchQuestion from './components/SwitchQuestion';

import { EXPLANATION_CHAR_LIMIT, PICTURE_SPOT_BASE_POSITIONS, PICTURE_SPOT_MAX_TABS } from './gameplay.constants';
import { Languages } from 'Models/Enum';

import { BaseContentFormValues } from '../ContentEdition/contentForm.hook';
import { ContentValidationError } from 'Store/entities/content/content.types';
import { translateApiErrors } from 'Services/api-error/api-error.service';

import generalStyles, { CONTENT_TEXT_AREA_WRAPPER_RADIUS } from './gameplay.style';
import styles from './PictureSpotForm.style';

const UButtonWithTooltip = withTooltip(UButton);

type PictureSpotFormProps = {
  language: keyof typeof Languages;
  isGameplayAlreadyChosen: boolean;
  editGameplay: () => void;
};

export const generateNewItem = (language: string) => {
  return {
    ...PICTURE_SPOT_BASE_POSITIONS,
    question: { [language]: '' },
    description: { [language]: '' },
  };
};
export const PictureSpotForm = ({ language, isGameplayAlreadyChosen, editGameplay }: PictureSpotFormProps) => {
  const { watch, setValue, formState } = useFormContext<BaseContentFormValues & { game: DeepPartial<PictureSpot> }>();
  const { root: errors } = formState.errors as unknown as { root: ContentValidationError };

  const [currentTab, setCurrentTab] = useState(0);

  const x = watch(`game.items.${currentTab}.x`) || PICTURE_SPOT_BASE_POSITIONS.x;
  const y = watch(`game.items.${currentTab}.y`) || PICTURE_SPOT_BASE_POSITIONS.y;
  const radius = watch(`game.items.${currentTab}.r`) || PICTURE_SPOT_BASE_POSITIONS.r;

  const tabs = watch('game.items') ?? [
    {
      x: PICTURE_SPOT_BASE_POSITIONS.x,
      y: PICTURE_SPOT_BASE_POSITIONS.y,
      r: PICTURE_SPOT_BASE_POSITIONS.r,
      question: { [language]: '' },
      description: { [language]: '' },
    },
  ];

  useEffect(() => {
    setValue('game.items', tabs);
  }, []);

  const addQuestion = () => {
    if (tabs.length === PICTURE_SPOT_MAX_TABS) {
      return;
    }

    setValue('game.items', [...(watch('game.items') || []), generateNewItem(language)]);
    setCurrentTab(tabs.length);
  };

  useEffect(() => {
    if (watch('game.items')?.length === 0) {
      setValue('game.items.0', generateNewItem(language));
    }
  }, [watch('game.items')]);

  const onChangePosition = useCallback(
    (x: number, y: number) => {
      setValue(`game.items.${currentTab}.x`, x);
      setValue(`game.items.${currentTab}.y`, y);
    },
    [setValue, currentTab],
  );

  const onChangeRadius = useCallback(
    (r: number) => {
      setValue(`game.items.${currentTab}.r`, r);
    },
    [setValue, currentTab],
  );

  const onChangeQuestion = useCallback(
    (text: string) => {
      setValue(`game.items.${currentTab}.question.${language}`, text);
    },
    [setValue, currentTab, language],
  );

  const onChangeDescription = useCallback(
    (text: string) => {
      setValue(`game.items.${currentTab}.description.${language}`, text);
    },
    [setValue, currentTab, language],
  );

  const onDeleteImageFromGame = useCallback(() => {
    setValue('game.image', undefined);
    setValue(`game.items.${currentTab}.x`, PICTURE_SPOT_BASE_POSITIONS.x);
    setValue(`game.items.${currentTab}.y`, PICTURE_SPOT_BASE_POSITIONS.y);
    setValue(`game.items.${currentTab}.r`, PICTURE_SPOT_BASE_POSITIONS.r);
  }, [setValue]);

  const uniqueKey = watch(`game.items.${currentTab}.id`);
  const imageErrors = errors?.[language]?.game?.image;
  const explanationErrors = errors?.[language]?.game?.explanation;

  return (
    <div style={{ ...generalStyles.wrapper, ...styles.column }}>
      <div style={{ ...generalStyles.container, ...styles.titleContainer }}>
        <SGameplayHeader
          iconType="picture-spot"
          title={t('gameplays:picture_spot.label')}
          description={t('gameplays:picture_spot.description')}
          onEditClick={editGameplay}
          isEditDisabled={isGameplayAlreadyChosen}
        />
      </div>
      <div style={styles.pictureSpotContainer}>
        <SPictureSpotImage
          onPositionChange={onChangePosition}
          onImageChange={(image) => {
            setValue('game.image', image);
          }}
          key={uniqueKey}
          imageUrl={watch('game.image.url')}
          initialZoneSize={radius}
          initialX={x}
          initialY={y}
          onChangeAnswerZoneSize={(r: number) => onChangeRadius(r)}
          onClearMedia={onDeleteImageFromGame}
        />
      </div>
      <div style={styles.pictureSpotError}>
        <UFieldError errorMessage={translateApiErrors(imageErrors ? { image: imageErrors } : {})} />
      </div>
      <div style={{ ...styles.tabsContainer, ...styles.commonContainer }}>
        <div style={styles.tabs}>
          <STabList
            bold
            selectedItem={currentTab}
            onChangeTab={setCurrentTab}
            tabs={tabs.map((_, index) => `${t('gameplays:picture_spot.question')} ${index + 1}`)}
          />
          <UButtonWithTooltip
            tooltipContent={t('gameplays:picture_spot.max_questions')}
            leftIcon="plus"
            disabled={tabs.length === PICTURE_SPOT_MAX_TABS}
            tooltipEnabled={tabs.length === PICTURE_SPOT_MAX_TABS}
            tooltipPosition="top"
            tooltipId="save-picture-spot"
            type="standard-light"
            text={t('gameplays:picture_spot.add_a_question')}
            onClick={addQuestion}
          />
        </div>
        <div style={styles.switchQuestion}>
          <SwitchQuestion
            errors={errors}
            language={language}
            tabs={tabs}
            question={watch(`game.items.${currentTab}.question.${language}`) || ''}
            description={watch(`game.items.${currentTab}.description.${language}`) || ''}
            currentTab={currentTab}
            onChangeQuestion={onChangeQuestion}
            onChangeDescription={onChangeDescription}
            setCurrentTab={setCurrentTab}
          />
        </div>
      </div>
      <div style={styles.commonContainer}>
        <STextArea
          maxLength={EXPLANATION_CHAR_LIMIT}
          radiusWrapper={CONTENT_TEXT_AREA_WRAPPER_RADIUS}
          textAreaTypo="BODY2"
          textAreaStyle={styles.textAreaBackground}
          placeholder={t('gameplays:picture_spot.explanation_placeholder')}
          label={t('gameplays:explanation')}
          labelSize="L"
          style={generalStyles.section}
          onChange={(text) => {
            setValue('game.explanation', { [language]: text });
          }}
          value={watch(`game.explanation.${language}`) || ''}
          errorMessage={translateApiErrors(explanationErrors ? { explanation: explanationErrors } : {})}
          dataTestContext="picture-spot-explanation"
        />
      </div>
    </div>
  );
};
